import type { HeavenlyStem } from '../types/stems';
import type { EarthlyBranch } from '../types/branches';
import type { Ohaeng } from '../types/elements';
import type { Relation, RelationOptions } from '../types/relations';
import type { PillarPosition } from '../types/pillars';
import { stemIndex } from '../core/sexagenary';
import { isAdjacentPillar } from './adjacent';
import { getBranchElement } from '../core/five-elements';

export const COMBINES: [number, number, Ohaeng][] = [
  [0, 5, '土'], // 甲己
  [1, 6, '金'], // 乙庚
  [2, 7, '水'], // 丙辛
  [3, 8, '木'], // 丁壬
  [4, 9, '火'], // 戊癸
];

export function findHeavenlyCombines(
  stems: { stem: HeavenlyStem; position: PillarPosition }[],
  monthBranch?: EarthlyBranch,
  options?: RelationOptions,
): Relation[] {
  const requireAdj = options?.requireAdjacency !== false;
  const blockContested = options?.blockContested !== false;
  const requireMonth = options?.requireMonthSupport !== false;

  const relations: Relation[] = [];

  for (let i = 0; i < stems.length; i++) {
    for (let j = i + 1; j < stems.length; j++) {
      // 인접성 체크
      if (requireAdj && !isAdjacentPillar(stems[i].position, stems[j].position)) continue;

      const a = stemIndex(stems[i].stem);
      const b = stemIndex(stems[j].stem);

      for (const [s1, s2, result] of COMBINES) {
        if ((a === s1 && b === s2) || (a === s2 && b === s1)) {
          const [first, second] = a === s1 ? [stems[i], stems[j]] : [stems[j], stems[i]];

          // 합화 조건 체크
          const transformed = requireMonth && monthBranch
            ? getBranchElement(monthBranch) === result
            : true;

          relations.push({
            type: '천간합',
            pillars: [stems[i].position, stems[j].position],
            stems: [first.stem, second.stem],
            result,
            transformed,
            description: transformed
              ? `${first.stem}${second.stem}합화 → ${result}`
              : `${first.stem}${second.stem}합 (합이불화)`,
          });
        }
      }
    }
  }

  // 쟁합/투합 차단: 같은 position이 2개 이상 합에 관여하면 모두 제거
  if (blockContested && relations.length > 1) {
    const posCount = new Map<string, number>();
    for (const r of relations) {
      for (const p of r.pillars) {
        posCount.set(p, (posCount.get(p) || 0) + 1);
      }
    }
    const contested = new Set([...posCount.entries()].filter(([, c]) => c > 1).map(([p]) => p));
    if (contested.size > 0) {
      return relations.filter(r => !r.pillars.some(p => contested.has(p)));
    }
  }

  return relations;
}
